import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('company', formData.company);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d607hlvnk1tv0692h06g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-gradient-to-br from-primary-dark to-primary relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="max-w-[1200px] mx-auto px-8 relative z-10">
        <div className="grid grid-cols-2 gap-16 items-center">
          {/* Left Side - Text */}
          <div className="space-y-8">
            <h2 className="text-5xl font-bold text-white leading-tight">
              Bereit für Ihre maßgeschneiderte Lösung?
            </h2>
            <p className="text-xl text-white/90 leading-relaxed">
              Lassen Sie uns gemeinsam die perfekte SaaS-Lösung für Ihr Business entwickeln. Unser Team steht bereit, Ihre Vision in die Realität umzusetzen.
            </p>

            {/* Feature List */}
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-white text-lg">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span>Kostenlose Erstberatung & Demo</span>
              </li>
              <li className="flex items-center gap-3 text-white text-lg">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span>Individuelle Lösungen für Ihre Branche</span>
              </li>
              <li className="flex items-center gap-3 text-white text-lg">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span>Schnelle Implementierung & Support</span>
              </li>
              <li className="flex items-center gap-3 text-white text-lg">
                <i className="ri-checkbox-circle-fill text-2xl"></i>
                <span>Transparente Preisgestaltung</span>
              </li>
            </ul>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-3xl p-12 shadow-2xl">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-14 px-4 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                  placeholder="Ihr vollständiger Name"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-14 px-4 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                  placeholder="ihre.email@firma.de"
                />
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  Firma *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full h-14 px-4 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                  placeholder="Ihr Firmenname"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nachricht *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300 resize-none"
                  placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anforderungen..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 Zeichen
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-16 bg-gradient-to-r from-primary to-primary-light text-white rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-xl hover:shadow-primary/50 flex items-center justify-center gap-3 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    Demo anfragen
                    <i className="ri-arrow-right-line text-xl"></i>
                  </>
                )}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm">
                  <i className="ri-checkbox-circle-fill mr-2"></i>
                  Vielen Dank! Wir melden uns in Kürze bei Ihnen.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm">
                  <i className="ri-error-warning-fill mr-2"></i>
                  Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
